import * as Yup from "yup";

/**
 * 서버(pydantic `EmailStr` · email-validator 2.2.0)가 **422 로 거절하는** 모양을 화면에서 먼저 막는다.
 *
 * Yup 의 `.email()` 은 WHATWG 규칙이라 서버보다 느슨하다. `gumin@naver`(.com 을 빠뜨린 오타 —
 * 가장 흔하다)를 통과시키고, 서버는 422 로 거절하고, 화면은 그걸 «잠시 후 다시 시도» 로
 * 말해서 사람은 **기다려도 영원히 안 되는** 주소로 다시 눌렀다(2026-09-23 공격 시험 f1-4).
 *
 * 규칙은 서버를 실측해서 옮겼다(2026-09-25, us-api venv). 화면이 서버보다 **엄하면 안 된다** —
 * 서버가 받는 `gu.min+tag@frism.co.kr` · `한글@frism.io` · `a@b.c0m` 을 막으면 그 사람은 가입도
 * 로그인도 못 한다. 그래서 «서버가 거절하는 것» 만 옮겼다(`emailRule.test.ts` 가 실측 표를 들고 있다).
 */
const SPECIAL_USE_TLDS = new Set(["arpa", "invalid", "local", "localhost", "onion", "test"]);

export function passesServerEmailRules(value: string): boolean {
  const at = value.lastIndexOf("@");
  if (at <= 0 || at === value.length - 1) return false;
  const local = value.slice(0, at);
  const domain = value.slice(at + 1);

  // 점으로 시작·끝나거나 두 번 연달아 — «cannot start with a period» 등
  if (local.startsWith(".") || local.endsWith(".") || local.includes("..")) return false;
  // `[1.2.3.4]` — «A bracketed IP address … is not allowed»
  if (domain.startsWith("[")) return false;

  const labels = domain.split(".");
  // `gumin@naver` — «It should have a period»
  if (labels.length < 2) return false;
  // 빈 라벨(`frism..io` · 끝 점) · 하이픈으로 시작하거나 끝나는 라벨
  if (labels.some((label) => !label || label.startsWith("-") || label.endsWith("-"))) return false;
  const tld = labels[labels.length - 1].toLowerCase();
  // `gumin@1.2.3.4` · `user@frism.123` — 숫자뿐인 최상위
  if (/^[0-9]+$/.test(tld)) return false;
  // `frism.local` · `frism.test` … — «a special-use or reserved name»
  if (SPECIAL_USE_TLDS.has(tld)) return false;
  return true;
}

/** 로그인·가입 신청이 같이 쓰는 이메일 칸 규칙. 두 화면이 따로 쓰면 한쪽만 고쳐져 갈라진다. */
export const emailField = (invalidMessage: string) =>
  Yup.string()
    .trim()
    .email(invalidMessage)
    .test("server-email-rules", invalidMessage, (value) => !value || passesServerEmailRules(value))
    .required("이메일을 입력해주세요.");
